import {postAjaxRequest} from "../../common/ajax";
import {Bubbles} from "./classes/bubbles";
import {SearchResults} from "./classes/search-results";
import {SearchInput} from "./classes/search-input";
import {HiddenList} from "./classes/hidden-list";
import {Pager} from "./classes/pager";

export class SearchSelect {

    constructor(container, ajaxUrl, csrfToken, onChange) {
        this.container = container;
        this.ajaxUrl = ajaxUrl;
        this.csrfToken = csrfToken;
        this.onChange = onChange;
        this.extraParams = [];

        this.hiddenElement = container.querySelector('input[type=hidden]');
        this.bubblesElement = container.getElementsByClassName('bubbles')[0];
        this.textInputElement = container.getElementsByTagName('textarea')[0];
        this.resultsContainerElement = container.getElementsByClassName('search-container')[0];
        this.extraParams = container.getElementsByClassName('extra-params')[0];
        this.pagerContainer = container.getElementsByClassName('pager-container')[0];
        this.clearSearch = container.getElementsByClassName('clear-search')[0];

        this.init();
    }

    init() {
        const extraParamsStr = this.extraParams.innerText.trim();
        try {
            this.extraParams = JSON.parse(extraParamsStr);
        } catch (error) {
        }

        this.select = new HiddenList(this.hiddenElement);

        this.bubbles = new Bubbles(this.bubblesElement, this.select.isMultiple, (item) => {
            this.select.deselectItem(item.value);

            // fire change event on hidden...
            this.select.dispatchChangeEvent();

            this.bubbles.update(this.select.getSelection(), this.select.getOptions());
            this.results.updateSelection(this.select.getSelection());
        });

        this.results = new SearchResults(this.resultsContainerElement, (item) => {
            this.selectionHandler(item);
        });

        this.pager = new Pager(this.pagerContainer, (idx, count) => {
            this.page(idx, count);
        });

        this.searchInput = new SearchInput(this.textInputElement, this.clearSearch, 250, (text) => {
            if (!text) {
                this.searchInput.toggleClearButton(false);
                this.pager.toggleControls(true);
                this.pager.reset();
                this.page(this.pager.idx, this.pager.count);
            } else {
                this.searchInput.toggleClearButton(true);
                this.pager.toggleControls(false);
                this.search(text);
            }
        });

        // Attach the click listener to the container to open/close itself.
        // Let the event propagate to the other click listeners, i.e. the document,
        // which will cause the select to close itself, or results container which
        // will keep the select open
        this.container.addEventListener('click', (evt) => {
            if (this.isOpen()) {
                this.close();
            } else {
                this.open();
            }
        });

        this.container.addEventListener('keydown', (evt) => {
            let hoverIndex = this.results.getHoveredIndex();

            if (evt.keyCode === 13) {
                evt.preventDefault();
                evt.stopPropagation();

                const hovered = this.results.getHovered();
                if (hovered) {
                    this.selectionHandler(hovered);
                }
            } else if (evt.keyCode === 38) {
                evt.preventDefault();
                if (hoverIndex > 0)
                    hoverIndex--;
                this.results.hover(hoverIndex);
            } else if (evt.keyCode === 40) {
                evt.preventDefault();
                if (hoverIndex < this.results.length() - 1)
                    hoverIndex++;
                this.results.hover(hoverIndex);
            }
        });

        // Attach the click cancel listener for the results popup. We don't want to close
        // the control when clicking inside here.
        this.results.attachClickCancelListener(this.documentClickCancelListener);

        // perform the initial search with no keywords
        this.initializeSelection();

        /**
         Called by either click on list item or pressing enter when hovered
         over an item. Fires change event and updates the control.
         * @param item
         */
        this.selectionHandler = (item) => {
            if (item.value === "") {
                this.select.clearSelection();
            }

            if (this.select.selectItem(item.value)) {
                // fire change event on hidden...
                this.select.dispatchChangeEvent();

                this.bubbles.update(this.select.getSelection(), this.select.getOptions());
                this.results.updateSelection(this.select.getSelection());
            }

            if (this.onChange) {
                this.onChange();
            }
            this.results.hover(-1);
            this.close();
        }
    };

    /**
     * @returns {boolean} True if the container is already open.
     */
    isOpen() {
        return this.container.className.indexOf('open') >= 0;
    };

    /**
     Opens the search input and results.
     */
    open() {
        this.container.className = 'search-select open';
        document.addEventListener('click', (e) => this.documentClickListener(e));
        this.searchInput.focus();
    };

    /**
     Collapses the search input and results.
     */
    close() {
        this.container.className = 'search-select';
        document.removeEventListener('click', (e) => this.documentClickListener(e));
    };

    /**
     Performs ajax request for given keywords. Callback will update control with new values.
     * @param keywords
     */
    search(keywords) {
        this.results.setSearching(true);
        const params = ['keywords', keywords, 'searchSelect', true, 'ajax', true, '_CSRFToken', this.csrfToken].concat(this.extraParams);
        postAjaxRequest(this.ajaxUrl, params, (result) => {
            this.results.setSearching(false);
            let jsonResp = {values: []};
            try {
                jsonResp = JSON.parse(result);
            } catch (error) {
            }

            this.select.setOptions(this.select.getSelection(), jsonResp.values);
            this.results.update(this.select.getSelection(), jsonResp);
            this.results.hover(-1);
        });
    };

    /**
     Performs ajax request for the selection that this control was initialized with. The server is only responsible
     for setting the selected values, not the options, so when initializing this control needs to go try and fetch
     the options for the current selection.
     */
    initializeSelection() {
        this.results.setSearching(true);

        const selected = this.select.getSelectionString();
        const params = ['selection', selected, 'searchSelect', true, 'ajax', true, '_CSRFToken', this.csrfToken].concat(this.extraParams);
        postAjaxRequest(this.ajaxUrl, params, (result) => {
            this.results.setSearching(false);
            let jsonResp = {values: []};
            try {
                jsonResp = JSON.parse(result);
            } catch (error) {
            }

            this.select.setOptions(this.select.getSelection(), jsonResp.values);
            this.bubbles.update(this.select.getSelection(), this.select.getOptions());
            this.results.update(this.select.getSelection(), jsonResp);
            this.results.hover(-1);
            this.pager.reset();
            this.page(this.pager.idx, this.pager.count);
        });
    };

    /**
     Performs ajax request for a 'page' of list items based on the given index and count.
     * @param idx the index of the beginning of the page
     * @param count the number of items to return
     */
    page(idx, count) {
        const params = ['idx', idx, 'count', count, 'searchSelect', true, 'ajax', true, '_CSRFToken', this.csrfToken].concat(this.extraParams);
        postAjaxRequest(this.ajaxUrl, params, (result) => {
            this.results.setSearching(false);
            let jsonResp = {values: []};
            try {
                jsonResp = JSON.parse(result);
            } catch (error) {
            }

            this.pager.update(jsonResp.idx, jsonResp.count, jsonResp.total);
            this.select.setOptions(this.select.getSelection(), jsonResp.values);
            this.results.update(this.select.getSelection(), jsonResp);
            this.results.hover(-1);

            this.results.recordsBeginIndexElement.innerText = this.pager.idx + 1;
            this.results.recordsEndIndexElement.innerText = Math.min(this.pager.idx + this.pager.count, jsonResp.total);
        });
    };

    disable() {
        this.container.disabled = true;
        this.container.style.pointerEvents = 'none';
        this.hiddenElement.disabled = true;
        this.bubblesElement.disabled = true;
        this.textInputElement.disabled = true;
        this.resultsContainerElement.disabled = true;
        this.extraParams.disabled = true;
        this.pagerContainer.disabled = true;
        this.clearSearch.disabled = true;
    }

    /**
     Event handler for clicks outside of the control.
     * @param evt
     */
    documentClickListener(evt) {
        let currParent = evt.target.parentElement;
        while (currParent) {
            if (currParent.id === this.container.id) {
                return;
            }
            currParent = currParent.parentElement;
        }
        this.close();
    };

    /**
     Event handler to attach to any and all elements that can be clicked on, where we
     do not want the control to be collapsed. I.e. When clicking INSIDE the control.
     * @param evt
     */
    documentClickCancelListener(evt) {
        evt.preventDefault();
        evt.stopPropagation();
    };
}
